import { useEffect, useState, type ReactElement } from 'react';
import clsx from 'clsx';
import { useStore } from '@nanostores/react';
import { Box, PageLoader, Panel } from '@customerio/pluma-components/react';
import { darkTheme, newTheme } from '@customerio/pluma-components/css';
import { sessionsStore } from '@customerio/hydra-session-state';
import { session } from '@/services/session';
import { SafeNavigate } from '@/services/router/safe-navigate';
import { SignupHeader } from './components/signup-header';
import { SignupPromoBanner } from './components/signup-promo-banner';
import { SignupStartupMarketing } from './components/signup-startup-marketing';
import { shouldShowTermsNotice, SignupTermsNotice } from './components/signup-terms-notice';
import { StepTransition } from './components/step-transition';
import { SignupFlowProvider, useSignupFlow } from './signup-flow';
import { PostRegistrationWait } from './post-registration-wait';
import { SignupAnalyticsEvent, signupTypeProps } from './utils/analytics';
import { resolveSignupColorScheme, type SignupColorScheme } from './utils/signup-color-scheme';
import type { SignupCompletion, SignupPageProps } from './types';
import type { SignupStep } from './utils/steps';
import { IndexStep } from './steps/index-step';
import { PasswordStep } from './steps/password-step';
import { CompanyStep } from './steps/company-step';
import { ConfirmationStep } from './steps/confirmation-step';
import { OnboardingUsageStep } from './steps/onboarding-usage-step';
import { ChannelsStep } from './steps/channels-step';
import { StartupCompleteStep } from './steps/startup-complete-step';
import * as styles from './index.css';

// A total Record keyed on SignupStep: adding a step without a view is a
// compile error, so this stays exhaustive as steps gain real components.
const STEP_VIEWS: Record<SignupStep, () => ReactElement> = {
  index: IndexStep,
  password: PasswordStep,
  company: CompanyStep,
  confirmation: ConfirmationStep,
  onboarding_usage: OnboardingUsageStep,
  channels: ChannelsStep,
  startup_complete: StartupCompleteStep,
};

/**
 * The onboarding steps lay their option cards out in 2- and 3-column grids and
 * need the wider stage; every other step keeps the narrow form width.
 */
const WIDE_PANEL_STEPS = new Set<SignupStep>(['onboarding_usage', 'channels']);

function panelMaxWidth(step: SignupStep): string {
  return WIDE_PANEL_STEPS.has(step) ? '720px' : '430px';
}

type SignupStepViewProps = {
  step: SignupStep;
};

function SignupStepView({ step }: SignupStepViewProps) {
  const StepView = STEP_VIEWS[step];
  return <StepView />;
}

/**
 * Forced-dark also themes the portals (the error snackbar) by putting the
 * Pluma dark theme class on `<body>`; the page wrapper carries it too so the
 * funnel itself never depends on the body class. Cleaned up on unmount — the
 * funnel exits by full-page redirect, but the flag can flip mid-session in dev.
 */
function useBodyDarkTheme(scheme: SignupColorScheme) {
  useEffect(() => {
    // Removes only what it added: another owner (Pluma's theme manager) may
    // already hold the class, and the funnel exits by SPA navigation — so an
    // unconditional cleanup would strip it for the rest of the session.
    if (scheme !== 'dark' || document.body.classList.contains(darkTheme.className)) {
      return;
    }
    document.body.classList.add(darkTheme.className);
    return () => {
      document.body.classList.remove(darkTheme.className);
    };
  }, [scheme]);
}

const WRAPPER_THEME_CLASS = {
  // `newTheme` is this app's ambient light theme, so forced-light matches what
  // an OS-light visitor already sees.
  light: newTheme.className,
  dark: darkTheme.className,
  default: false,
} satisfies Record<SignupColorScheme, string | false>;

/** The startup application's 1200px marketing/form split. Entry step only. */
function StartupStage({ step }: Pick<SignupPageProps, 'step'>) {
  return (
    <Box as="main" className={styles.startupStage} style={{ minHeight: 'calc(100vh - 40px)' }}>
      <Box w="full" style={{ maxWidth: '1200px' }}>
        <Box className={styles.startupSplit}>
          <Box className={styles.startupMarketingCol}>
            <SignupStartupMarketing />
          </Box>
          <Box className={styles.startupFormCol}>
            <Box className={styles.startupFormSurface}>
              <Panel w="full" style={{ border: 'none' }}>
                <SignupStepView step={step} />
              </Panel>
            </Box>
          </Box>
        </Box>
        <Box mt="200">
          <SignupTermsNotice className={styles.startupTermsNotice} />
        </Box>
      </Box>
    </Box>
  );
}

type StandardStageProps = Pick<SignupPageProps, 'step'> & {
  isDark: boolean;
};

/** The funnel's centered panel — width follows the step — promo banner above, terms notice below. */
function StandardStage({ step, isDark }: StandardStageProps) {
  const { validatedPromo } = useSignupFlow();

  // Entry step only: the code arrives in the URL, and past the entry step the
  // visitor has committed to the funnel the code came with.
  const showPromoBanner = step === 'index' && validatedPromo != null;

  return (
    <Box
      as="main"
      display="flex"
      alignItems="flex-start"
      justifyContent="center"
      pb="500"
      pt="600"
      px="300"
      style={{ minHeight: 'calc(100vh - 40px)' }}
    >
      <Box w="full" style={{ maxWidth: panelMaxWidth(step) }} data-testid="signup-panel-stage">
        {showPromoBanner ? <SignupPromoBanner promo={validatedPromo} /> : null}
        <StepTransition stepKey={step}>
          <Panel mb="300" w="full" className={clsx(isDark && styles.darkCard)}>
            <SignupStepView step={step} />
          </Panel>
        </StepTransition>
        {shouldShowTermsNotice(step) ? <SignupTermsNotice /> : null}
      </Box>
    </Box>
  );
}

/**
 * The funnel shell: logo header plus the current step's stage. The startup
 * application swaps the centered panel for a wide marketing/form split on the
 * entry step; every other route through the funnel uses the standard stage.
 */
function SignupContent({ step }: Pick<SignupPageProps, 'step'>) {
  const { effectiveSignupType, trackSignupEvent, isStartup, isSignupDarkModeEnabled } = useSignupFlow();

  const scheme = resolveSignupColorScheme({ isStartup, isSignupDarkModeEnabled });
  const isDark = scheme === 'dark';
  useBodyDarkTheme(scheme);

  const isStartupFormStep = isStartup && step === 'index';
  const surfaceClass = isStartupFormStep ? styles.startupSurface : styles.signupSurface;

  return (
    <Box
      className={clsx(WRAPPER_THEME_CLASS[scheme], isDark ? styles.darkWrapper : surfaceClass)}
      style={{ minHeight: '100vh' }}
    >
      <SignupHeader
        // The startup application pitches its own path; the log-in callout
        // would point away from it.
        showLoginLink={step === 'index' && !isStartupFormStep}
        onLoginLinkClick={() =>
          trackSignupEvent(SignupAnalyticsEvent.SignInClicked, signupTypeProps({ signupType: effectiveSignupType }))
        }
        isDark={isDark}
      />
      {isStartupFormStep ? <StartupStage step={step} /> : <StandardStage step={step} isDark={isDark} />}
    </Box>
  );
}

export default function SignupPage(props: SignupPageProps) {
  const isAuthenticated = useStore(session.isAuthenticated);
  const isInitialized = useStore(session.isInitialized);
  const hasPersistedSession = useStore(sessionsStore).length > 0;
  // Set only by a register completing in this page — the flow provider hands
  // the payload over right before the session flip unmounts it. Stays `null`
  // for an already-authenticated visitor who lands on /signup, so only a real
  // completion waits out readiness and fires `Signup: Completed`.
  const [completion, setCompletion] = useState<SignupCompletion | null>(null);

  // While a persisted session exists but the `/accounts/current` bootstrap
  // hasn't resolved, `isAuthenticated` is still `false` — we don't yet know
  // whether to redirect. Show a loader instead of flashing the form. A visitor
  // with no persisted session is definitely logged out, so the form renders
  // immediately.
  if (hasPersistedSession && !isInitialized) {
    return <PageLoader />;
  }

  // Already-authenticated users don't belong on the signup funnel — send them
  // to the app. Unconditional across every step: the funnel runs entirely
  // unauthenticated and only authenticates at its very end (the flow
  // provider's end-of-sequence session flip lands here). A completed signup
  // holds for account readiness first; anyone else redirects immediately.
  if (isAuthenticated) {
    if (completion != null) {
      return <PostRegistrationWait completion={completion} />;
    }
    return <SafeNavigate to="/" options={{ shouldReplaceHistoryEntry: true }} />;
  }

  return (
    <SignupFlowProvider
      step={props.step}
      onNavigateStep={props.onNavigateStep}
      signupType={props.signupType}
      promoCode={props.promoCode}
      initialFormValues={props.initialFormValues}
      resumeEmail={props.resumeEmail}
      onSignupComplete={setCompletion}
    >
      <SignupContent step={props.step} />
    </SignupFlowProvider>
  );
}
